/**
 * The design system's `<ViewTransition>` (Issue #14363).
 *
 * A thin wrapper over React's `<ViewTransition>` that assigns the motion
 * classes `globals.css` styles for each trigger, so a caller writes
 * `<ViewTransition>` and gets the shared timings instead of hand-tuning
 * `::view-transition-*` rules per feature:
 *
 *   - `enter`  — the incoming snapshot fades and rises (`--motion-duration-base`,
 *                `--motion-ease-out`)
 *   - `exit`   — the outgoing snapshot fades (`--motion-duration-fast`)
 *   - `update` — the group morphs size/position (`--motion-duration-base`,
 *                `--motion-ease-in-out`)
 *
 * Any trigger can be overridden, including with `'none'` to opt out of it.
 * Under `prefers-reduced-motion: reduce`, a global rule in `globals.css` turns
 * EVERY view transition into a plain crossfade — callers do nothing.
 *
 * Animations only run for Transitions, `<Suspense>` reveals and
 * `useDeferredValue`; App Router navigations are Transitions. See
 * `docs/design-system/PATTERN_REFERENCE.md` § Motion.
 *
 * WHERE IT IS UNSUPPORTED, IT ANIMATES NOTHING
 *
 * `ViewTransition` is exported by the React canary the App Router bundles,
 * not by the stable `react` package (the one unit tests and any non-Next
 * consumer resolve). It is read off the namespace rather than imported by
 * name so that a React without it degrades to rendering the children as-is,
 * instead of rendering `undefined` as an element type. In a browser without
 * the View Transitions API, React itself commits without animating.
 */

import * as React from 'react'
import type { ComponentType, ReactNode } from 'react'

/** The `view-transition-class` each trigger gets unless the caller overrides. */
export const VIEW_TRANSITION_CLASSES = {
  enter: 'motion-enter',
  exit: 'motion-exit',
  update: 'motion-update',
} as const

/**
 * A class name, `'none'` to deactivate the trigger, `'auto'` for the
 * browser's default crossfade, or a per-transition-type map.
 */
export type ViewTransitionClass = string | Record<string, string>

export interface ViewTransitionProps {
  children?: ReactNode
  /** Shared-element identity; the same name on two routes morphs between. */
  name?: string
  enter?: ViewTransitionClass
  exit?: ViewTransitionClass
  update?: ViewTransitionClass
  share?: ViewTransitionClass
  default?: ViewTransitionClass
}

type NativeViewTransition = ComponentType<ViewTransitionProps>

const EXPORT_NAMES = ['ViewTransition', 'unstable_ViewTransition'] as const

/**
 * React's `ViewTransition`, or `null` when this React build lacks it.
 *
 * Probed with `in` before reading: a strict module mock (the unit suite's
 * `react` mock is one) throws on reading an export it does not define.
 */
function nativeViewTransition(): NativeViewTransition | null {
  for (const exportName of EXPORT_NAMES) {
    if (!(exportName in React)) continue
    const value: unknown = Reflect.get(React, exportName)
    if (value != null) return value as NativeViewTransition
  }
  return null
}

/** Resolved once: the React build does not change between renders. */
const NativeViewTransition = nativeViewTransition()

/**
 * React's `<ViewTransition>` with the design system's motion classes.
 *
 * Precedence per trigger, matching React's own: an explicit `enter` / `exit`
 * / `update` prop, then the caller's `default`, then the shared motion
 * class. So `default="none"` switches off every trigger not named
 * explicitly, which is how a named `share="morph"` pair stays still during
 * unrelated transitions.
 *
 * @example
 * <Suspense fallback={<NoteSkeleton />}>
 *   <ViewTransition>
 *     <NoteBody />
 *   </ViewTransition>
 * </Suspense>
 */
export function ViewTransition({
  children,
  enter,
  exit,
  update,
  ...rest
}: ViewTransitionProps) {
  if (NativeViewTransition === null) return <>{children}</>
  return (
    <NativeViewTransition
      enter={enter ?? rest.default ?? VIEW_TRANSITION_CLASSES.enter}
      exit={exit ?? rest.default ?? VIEW_TRANSITION_CLASSES.exit}
      update={update ?? rest.default ?? VIEW_TRANSITION_CLASSES.update}
      {...rest}
    >
      {children}
    </NativeViewTransition>
  )
}
